import { createSelector } from "@reduxjs/toolkit";
import { StoreState } from "../store";
import { doseSelectors } from "./doseSlice";
import { CustomizationBase, CustomizationBaseWithModelName, CustomizationObjectType, CustomizationOutput, Model, ModelVisibility, ValidationStatus, worstValidationStatus } from "../global-types/customization-types";
import { DoseRoi, DoseTarget, isDoseCustomizationOutput } from "./dose-types";
import { BackendValidationErrorViewModel, FormValidationError, FormValidationErrorSeverity } from "../global-types/store-errors";
import { generateCustomizationTreeViewItems, SelectorWithInputArg } from "../global-types/customizationSelectors";
import { naturalSort } from "../../util/sort";
import { CommonDoseSelectors } from "../global-types/modelTypeSelectorHelper/commonDoseSelectors";


const commonSelectors = new CommonDoseSelectors();

export const doseCommonSelectors = {
    selectDeprecatedModels: (state: StoreState) => commonSelectors.selectDeprecatedModels(state.dose),
    selectNonLicensedModels: (state: StoreState) => commonSelectors.selectNonLicensedModels(state.dose),
    selectUserHiddenModels: (state: StoreState) => commonSelectors.selectUserHiddenModels(state.dose),
    selectLatestModels: (state: StoreState) => commonSelectors.selectLatestModels(state.dose),
    selectModelsInAlphabeticalOrder: (state: StoreState) => commonSelectors.selectModelsInAlphabeticalOrder(state.dose),
    selectAllVersionTagsInOrder: (state: StoreState) => commonSelectors.selectAllVersionTagsInOrder(state.dose),
    selectAllTagsInOrder: (state: StoreState) => commonSelectors.selectAllTagsInOrder(state.dose),
    selectInputModalities: (state: StoreState) => commonSelectors.selectInputModalities(state.dose),
    selectOutputModalities: (state: StoreState) => commonSelectors.selectOutputModalities(state.dose),
}


/** Generate a tree view hierarchy of models and some of their child objects. This is used by the customization page side navigation.
 * This data isn't ready-to-go for Patternfly's TreeView component but the separate convertTreeViewDataIntoItemHierarchy helper function
 * in customization-helpers.tsx is used to generate the appropriate data from what this function produces.
 */
export const selectDoseTreeViewItems = createSelector([
    (state: StoreState) => doseCommonSelectors.selectModelsInAlphabeticalOrder(state),
    (state: StoreState) => doseSelectors.selectCustomizationBasesInAlphabeticalOrder(state),
    (state: StoreState) => doseSelectors.selectOutputEntities(state),
    (state: StoreState) => doseSelectors.selectOutputMetadataEntities(state),
    (state: StoreState) => doseSelectors.selectCustomizationValidationErrorEntities(state),
    (state: StoreState) => doseSelectors.selectFormValidationErrors(state),
    (state: StoreState) => doseSelectors.selectDicomRuleEntities(state),
],
    (models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, dicomRuleEntities) => {

        // create an inline validation function that will be supplied to tree view generator function
        const areOutputChildrenValid = (output: CustomizationOutput, validationEntities: Record<string, BackendValidationErrorViewModel>, formErrors: FormValidationError[]): ValidationStatus => {
            if (!isDoseCustomizationOutput(output)) {
                throw new Error('Invalid output type');
            }
            const hasBackendRoiError = !output.rois.every(rId => !validationEntities[rId] || validationEntities[rId]!.type !== CustomizationObjectType.Roi);
            const hasBackendTargetError = !output.targets.every(tId => !validationEntities[tId] || validationEntities[tId]!.type !== CustomizationObjectType.Target);
            let status: ValidationStatus = (hasBackendRoiError || hasBackendTargetError) ? ValidationStatus.Error : ValidationStatus.Valid;

            // Check form errors on ROIs and targets belonging to this output
            for (const err of formErrors) {
                if (output.rois.includes(err.itemId) || output.targets.includes(err.itemId)) {
                    if (err.severity === FormValidationErrorSeverity.Error) {
                        return ValidationStatus.Error;
                    }
                    if (err.severity === FormValidationErrorSeverity.Warning) {
                        status = worstValidationStatus(status, ValidationStatus.Warning);
                    }
                }
            }

            return status;
        }

        const results = generateCustomizationTreeViewItems(models, customizationBases, outputEntities, metadataEntities, validationEntities, formValidationErrors, areOutputChildrenValid, dicomRuleEntities);
        return results;
    });


/** Factory function for making a memoized selector that returns all form validation errors for ring distances of a specific output. */
export const makeSelectFormValidationErrorsForOptimization = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, outputId: string) => outputId, doseSelectors.selectFormValidationErrors],
    (outputId, formValidationErrors) => formValidationErrors.filter(
        e => e.itemId === outputId && e.itemType === CustomizationObjectType.CustomizationOutput
    )
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific roi customization. */
export const makeSelectFormValidationErrorsForRoi = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, roiId: string) => roiId, doseSelectors.selectFormValidationErrors],
    (roiId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === roiId && e.itemType === CustomizationObjectType.Roi)
);

/** Factory function for making a memoized selector that returns all dose roi objects that belong to a specific dose output. */
export const makeSelectDoseRoisForOutput = (): SelectorWithInputArg<DoseRoi[], string> => createSelector(
    [doseSelectors.selectRois, (_, outputId: string) => outputId],
    (doseRois, outputId) => doseRois.filter(r => r.outputId === outputId)
);

/** Factory function for making a memoized selector that returns all dose target objects that belong to a specific dose output. */
export const makeSelectDoseTargetsForOutput = (): SelectorWithInputArg<DoseTarget[], string> => createSelector(
    [doseSelectors.selectTargets, (_, outputId: string) => outputId],
    (doseTargets, outputId) => doseTargets.filter(r => r.outputId === outputId)
);

/**
* Factory function for making a memoized selector that returns true if any metadata for given output id is modified.
*/
export const makeSelectIsAnyMetadataForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationOutputId: string) => doseSelectors.selectOutputById(state, customizationOutputId),
        (state: StoreState) => doseSelectors.selectOutputMetadataEntities(state)
    ],
    (customizationOutput, metadataEntities) => customizationOutput?.metadata.some(mId => metadataEntities[mId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any dose ROI for given output id is modified.
 */
export const makeSelectIsAnyRoiForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, outputId) => doseSelectors.selectOutputById(state, outputId),
        (state: StoreState) => doseSelectors.selectRoiEntities(state)
    ],
    (customizationOutput, roiCustomizationEntities) => customizationOutput?.rois.some(rId => roiCustomizationEntities[rId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any dose target for given output id is modified.
 */
export const makeSelectIsAnyTargetForOutputModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, outputId) => doseSelectors.selectOutputById(state, outputId),
        (state: StoreState) => doseSelectors.selectTargetEntities(state)
    ],
    (customizationOutput, targetCustomizationEntities) => customizationOutput?.targets.some(tId => targetCustomizationEntities[tId]?.isModified) || false
);

/**
 * Factory function for making a memoized selector that returns true if any selection rule (trigger) for a customization base id is modified.
 */
export const makeSelectIsAnyTriggerForBaseModified = (): SelectorWithInputArg<boolean, string> => createSelector(
    [
        (state: StoreState, customizationBaseId) => doseSelectors.selectCustomizationBaseById(state, customizationBaseId),
        (state: StoreState) => doseSelectors.selectAeTitleRuleEntities(state),
        (state: StoreState) => doseSelectors.selectDicomRuleEntities(state)
    ],
    (customizationBase, aeTitleRuleEntities, dicomRuleEntities) => {
        const isAnyAeTitleRuleModified = customizationBase?.aeTitleRules.some(aId => aeTitleRuleEntities[aId]?.isModified) || false;
        if (isAnyAeTitleRuleModified) { return true; }
        return customizationBase?.dicomRules.some(dId => dicomRuleEntities[dId]?.isModified === true) || false;
    }
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom rule. */
export const makeSelectFormValidationErrorsForDicomRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomRuleId: string) => dicomRuleId, doseSelectors.selectFormValidationErrors],
    (dicomRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomRuleId && e.itemType === CustomizationObjectType.DicomRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific dicom attribute rule. */
export const makeSelectFormValidationErrorsForDicomAttributeRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, dicomAttributeRuleId: string) => dicomAttributeRuleId, doseSelectors.selectFormValidationErrors],
    (dicomAttributeRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === dicomAttributeRuleId && e.itemType === CustomizationObjectType.DicomAttributeRule)
);

/** Factory function for making a memoized selector that returns all form validation errors objects that are related to a specific AE Title rule. */
export const makeSelectFormValidationErrorsForAeTitleRule = (): SelectorWithInputArg<FormValidationError[], string> => createSelector(
    [(_, aeTitleRuleId: string) => aeTitleRuleId, doseSelectors.selectFormValidationErrors],
    (aeTitleRuleId, formValidationErrors) => formValidationErrors.filter(e => e.itemId === aeTitleRuleId && e.itemType === CustomizationObjectType.AeTitleRule)
);

/**
 * Returns customization base IDs and their names in alphabetical order.
 */
export const selectCustomizationBasesInNameOrder = createSelector(
    [(state: StoreState): CustomizationBase[] => doseSelectors.selectCustomizationBases(state),
    (state: StoreState): Record<string, Model> => doseSelectors.selectModelEntities(state),
    (state: StoreState) => state.dose.areDeprecatedModelsShown,
    (state: StoreState) => state.dose.areNonLicensedModelsShown,
    (state: StoreState) => state.dose.areUserHiddenModelsShown],
    (customizations, modelEntities, areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown) => {

        // filter customizations per current user config
        const filteredCustomizations = customizations.filter(c => {
            const model = modelEntities[c.modelId];
            if (!model) { throw new Error(`Could not find model for customization ${c.customizationName} (${c.id})`); }
            if (model.visibility === ModelVisibility.AlwaysShown) { return true; }
            if (model.visibility === ModelVisibility.AlwaysHidden) { return areUserHiddenModelsShown; }
            if (!model.isAvailable && !areNonLicensedModelsShown) { return false; }
            if (model.isDeprecated && !areDeprecatedModelsShown) { return false; }
            return true;
        })

        const customizationNamesWithIds: CustomizationBaseWithModelName[] = filteredCustomizations.map(c => ({
            customizationBaseId: c.id,
            label: `${modelEntities[c.modelId]?.label || ''} ${c.customizationName}`
        }));

        return naturalSort(customizationNamesWithIds, 'label');
    });

/**
 * Selector to map model version tags to matching customization bases. 
 */
export const selectVersionTagsForCustomizationBases = createSelector([
    (state: StoreState) => doseSelectors.selectModels(state),
    (state: StoreState) => doseCommonSelectors.selectAllVersionTagsInOrder(state),
], (models, allVersionTags) => {
    // create a map of version tags (string[]) for customization bases (ids)
    const mapOfVersionTags: Record<string, string[]> = {};

    for (const model of models) {
        const versionTags: string[] = [];
        if (model.isLatest) {
            versionTags.push('latest');
        }
        if (model.version) {
            versionTags.push(model.version);
        }
        
        model.customizations.forEach(customizationBaseId => mapOfVersionTags[customizationBaseId] = versionTags);
    }

    return mapOfVersionTags;
});
